'use client';

import { useRef } from 'react';

interface UseSwipeGestureProps {
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
  threshold?: number; // Minimum distance in pixels to trigger swipe
}

interface UseSwipeGestureReturn {
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onTouchEnd: () => void;
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: () => void;
}

/**
 * Hook to handle swipe gestures for toggling between AI and Form modes
 * Supports both touch and pointer events for mobile and desktop compatibility
 */
export function useSwipeGesture({
  onSwipeUp,
  onSwipeDown,
  threshold = 50, // Default 50px threshold
}: UseSwipeGestureProps): UseSwipeGestureReturn {
  const startY = useRef<number>(0);
  const currentY = useRef<number>(0);
  const isDragging = useRef<boolean>(false);

  const onTouchStart = (e: React.TouchEvent) => {
    startY.current = e.touches[0].clientY;
    currentY.current = e.touches[0].clientY;
    isDragging.current = true;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current) return;
    currentY.current = e.touches[0].clientY;
  };

  const onTouchEnd = () => {
    if (!isDragging.current) return;

    const deltaY = currentY.current - startY.current;

    if (Math.abs(deltaY) >= threshold) {
      if (deltaY < 0 && onSwipeUp) {
        onSwipeUp();
      } else if (deltaY > 0 && onSwipeDown) {
        onSwipeDown();
      }
    }

    isDragging.current = false;
    startY.current = 0;
    currentY.current = 0;
  };

  const onPointerDown = (e: React.PointerEvent) => {
    startY.current = e.clientY;
    currentY.current = e.clientY;
    isDragging.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!isDragging.current) return;
    currentY.current = e.clientY;
  };

  const onPointerUp = () => {
    if (!isDragging.current) return;

    const deltaY = currentY.current - startY.current;

    if (Math.abs(deltaY) >= threshold) {
      if (deltaY < 0 && onSwipeUp) {
        onSwipeUp();
      } else if (deltaY > 0 && onSwipeDown) {
        onSwipeDown();
      }
    }

    isDragging.current = false;
    startY.current = 0;
    currentY.current = 0;
  };

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onPointerDown,
    onPointerMove,
    onPointerUp,
  };
}
